/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import EditIcon from '@atlaskit/icon/core/edit';
import { Box } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { messages } from '../../common/messages';

const labelStyles = css({
	display: 'block',
	color: token('color.text.subtle'),
	marginBottom: token('space.075'),
});

const promptSectionStyles = css({
	borderTopColor: token('color.border'),
	borderTopStyle: 'solid',
	borderTopWidth: token('border.width'),
	paddingTop: token('space.150'),
});

const promptToggleStyles = css({
	appearance: 'none',
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	borderWidth: 0,
	backgroundColor: 'transparent',
	color: token('color.text.subtle'),
	cursor: 'pointer',
	font: 'inherit',
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
	textAlign: 'left',
	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.050'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const promptToggleTextStyles = css({
	marginLeft: token('space.075'),
});

const promptToggleChevronStyles = css({
	marginLeft: 'auto',
});

const promptBodyStyles = cssMap({
	root: {
		marginTop: token('space.100'),
	},
});

type PromptPanelProps = {
	isDisabled?: boolean;
	isExpanded: boolean;
	onPromptChange: (prompt: string) => void;
	onToggle: () => void;
	prompt: string;
};

export const PromptPanel = ({
	isDisabled = false,
	isExpanded,
	onPromptChange,
	onToggle,
	prompt,
}: PromptPanelProps): JSX.Element => {
	const intl = useIntl();
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	useEffect(() => {
		if (isExpanded) {
			textareaRef.current?.focus({ preventScroll: true });
		}
	}, [isExpanded]);

	return (
		<div css={promptSectionStyles}>
			<button
				aria-expanded={isExpanded}
				css={promptToggleStyles}
				disabled={isDisabled}
				onClick={onToggle}
				type="button"
			>
				<EditIcon label="" color="currentColor" />
				<span css={promptToggleTextStyles}>{intl.formatMessage(messages.promptToggleLabel)}</span>
				<span css={promptToggleChevronStyles}>
					{isExpanded ? (
						<ChevronUpIcon label="" color="currentColor" />
					) : (
						<ChevronDownIcon label="" color="currentColor" />
					)}
				</span>
			</button>
			{isExpanded && (
				<Box xcss={promptBodyStyles.root}>
					<label css={labelStyles} htmlFor="agent-managed-extension-prompt">
						{intl.formatMessage(messages.customInstructionsLabel)}
					</label>
					<TextArea
						id="agent-managed-extension-prompt"
						testId="agent-managed-extension-prompt"
						ref={textareaRef}
						minimumRows={3}
						value={prompt}
						onChange={(event) => onPromptChange(event.currentTarget.value)}
						placeholder={intl.formatMessage(messages.customInstructionsPlaceholder)}
						isDisabled={isDisabled}
					/>
				</Box>
			)}
		</div>
	);
};
